
import React, { FunctionComponent } from "react";
import { Box } from "@mui/material";
import { SitesEmissionsDTO } from "api";

import {
  COLORS,
  Label12Regular,
  Label14SemiBold,
  Label16Medium,
  Label16SemiBold,
  Label18SemiBold,
  Loading,
} from "components";

import { toUSNumber } from "utils";
import { ScopeEmissionPieChart } from "./emission-summary-pieChart";
import { ScopeEmissionScope2PieChart } from "./emission-summary-scope2-pieChart";
import { ScopeEmissionScopePieChart } from "./emission-summary-scope-pieChart";
import { ScopeEmissionScope2MarketPieChart } from "./emission-summary-scope2-market-pieChart";

export const ConsumptionSummary: FunctionComponent<{
  sitesEmissions: Omit<SitesEmissionsDTO, "siteAggregations">;
  isLoading: boolean;
}> = ({ sitesEmissions, isLoading }) => (
  isLoading
    ? <Loading />
    : <>
      <Box sx={{
        width: "100%",
        display: "flex",
        gap: 2,
        mt: 2,
      }}>
        <Box sx={{
          display: "flex",
          width: "60%",
          gap: "16px",
          overflowX: "auto",
        }}>
          <ScopeEmissionPieChart sitesEmissionsData={sitesEmissions} />
          <Box sx={{
            position: "relative",
            height: "317px",
            maxWidth: "230px",
            minWidth: "230px",
            borderRadius: "8px",
            border: "1px solid rgba(5, 65, 84, 0.5)",
          }}>
            <Box sx={{ top: 0, position: "absolute" }}>
              <ScopeEmissionScopePieChart
                sitesEmissionsData={sitesEmissions}
              />
            </Box>
          </Box>
          <Box sx={{
            position: "relative",
            height: "317px",
            maxWidth: "230px",
            minWidth: "230px",
            borderRadius: "8px",
            border: "1px solid rgba(5, 65, 84, 0.5)",
          }}>
            <Box sx={{ top: 0, position: "absolute" }}>
              <ScopeEmissionScope2PieChart
                sitesEmissionsData={sitesEmissions}
              />
            </Box>
          </Box>
          <Box sx={{
            position: "relative",
            height: "317px",
            maxWidth: "230px",
            minWidth: "230px",
            borderRadius: "8px",
            border: "1px solid rgba(5, 65, 84, 0.5)",
          }}>
            <Box sx={{ top: 0, position: "absolute" }}>
              <ScopeEmissionScope2MarketPieChart
                sitesEmissionsData={sitesEmissions}
              />
            </Box>
          </Box>
        </Box>
        <Box sx={{
          display: "flex",
          height: "317px",
          minWidth: "400px",
          maxWidth: "680px",
          width: "40%",
          borderRadius: "8px",
          border: "1px solid rgba(5, 65, 84, 0.5)",
        }}>
          <Box sx={{
            minWidth: "fit-content",
            height: "100%",
            borderRight: "1px solid rgba(5, 65, 84, 0.5)",
            padding: "16px",
          }}>
            <Label14SemiBold>Sites</Label14SemiBold>
            <h3 style={{ fontSize: "18px", fontWeight: "600", color: COLORS.Mainblue }}>{`${sitesEmissions.selectedSites} / ${sitesEmissions.totalSites}`}</h3>
          </Box>
          <Box sx={{
            width: "100%",
            padding: "16px",
          }}>
            <Label14SemiBold>Consumption</Label14SemiBold>
            <Box sx={{ overflowY: "auto", height: "100%", paddingRight: "8px" }}>
              {sitesEmissions.utilityTypesUsage?.map((ut: any) => (
                <Box sx={{
                  width: "100%",
                  minHeight: "40px",
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "4px",
                  border: "1px solid #054254",
                  mt: "12px",
                  color: "#054254",
                  fontSize: "14px",
                  fontWeight: "600",
                }}>
                  <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    minWidth: "160px",
                    maxWidth: "160px",
                    height: "100%",
                    borderRight: "1px solid #054254",
                    px: "8px",
                  }}>
                    <p>{ut.utilityType}</p>
                  </Box>
                  <Box sx={{
                    display: "flex",
                    padding: "0 8px",
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "end",
                  }}>
                    {toUSNumber(ut.usage)} {ut.unit}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "240px",
        width: "100%",
        my: "16px",
        borderRadius: "8px",
        border: "1px solid rgba(5, 65, 84, 0.5)",
        padding: "16px",
      }}>
        <Label18SemiBold
          sx={{ fontSize: 20, fontWeight: "700" }}
        >
          Company Intensity Metrics
        </Label18SemiBold>
        <Box sx={{
          display: "flex",
          gap: 2,
          marginTop: 2,
        }}>
          <Box sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            borderRadius: "8px",
            border: "1px solid rgba(5, 65, 84, 0.5)",
          }}>
            <Label18SemiBold
              sx={{ fontSize: 18, fontWeight: "700", color: "#054254", py: "8px", pl: "16px" }}
            >
              Production Unit
            </Label18SemiBold>
            <Box sx={{
              borderTop: "1px solid rgba(5, 65, 84, 0.5)",
              display: "flex",
              flexDirection: "column",
            }}>
              <Box sx={{ p: "16px" }}>
                <Label16SemiBold>
                  Scope 1 + Scope 2 (Location)
                </Label16SemiBold>
                {sitesEmissions?.totalIntensityProductionCo2Emission !==
                  null ? (
                  <Box sx={{ display: "flex", mt: "7px" }}>
                    <Label16Medium>
                      {sitesEmissions?.totalIntensityProductionCo2Emission}
                    </Label16Medium>
                    <span style={{ marginTop: "2px", marginLeft: "7px" }}>
                      <Label12Regular>
                        tCO2e/{sitesEmissions?.intensityProductionUnit}
                      </Label12Regular>
                    </span>
                  </Box>
                ) : (
                  <Label12Regular
                    style={{ color: "#C1C5CE", fontStyle: "italic" }}
                  >
                    No data provided
                  </Label12Regular>
                )}
              </Box>
              <Box sx={{ p: "16px", paddingTop: 0 }}>
                <Label16SemiBold>Scope 1 + Scope 2 (Market)</Label16SemiBold>
                {sitesEmissions?.totalMarketIntensityProductionCo2Emission !==
                  null ? (
                  <Box sx={{ display: "flex", mt: "7px" }}>
                    <Label16Medium>
                      {
                        sitesEmissions?.totalMarketIntensityProductionCo2Emission
                      }
                    </Label16Medium>
                    <span style={{ marginTop: "2px", marginLeft: "7px" }}>
                      <Label12Regular>
                        tCO2e/{sitesEmissions?.intensityProductionUnit}
                      </Label12Regular>
                    </span>
                  </Box>
                ) : (
                  <Label12Regular
                    style={{ color: "#C1C5CE", fontStyle: "italic" }}
                  >
                    No data provided
                  </Label12Regular>
                )}
              </Box>
            </Box>
          </Box>
          <Box sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            borderRadius: "8px",
            border: "1px solid rgba(5, 65, 84, 0.5)",
          }}>
            <Label18SemiBold
              sx={{ fontSize: 18, fontWeight: "700", color: "#054254", py: "8px", pl: "16px" }}
            >
              Company Revenue
            </Label18SemiBold>
            <Box sx={{
              borderTop: "1px solid rgba(5, 65, 84, 0.5)",
              display: "flex",
              flexDirection: "column",
            }}>
              <Box sx={{ p: "16px" }}>
                <Label16SemiBold>
                  Scope 1 + Scope 2 (Location)
                </Label16SemiBold>
                {sitesEmissions?.totalIntensityRevenueCo2Emission !== null ? (
                  <Box sx={{ display: "flex", mt: "7px" }}>
                    <Label16Medium>
                      {sitesEmissions?.totalIntensityRevenueCo2Emission}
                    </Label16Medium>
                    <span style={{ marginTop: "2px", marginLeft: "7px" }}>
                      <Label12Regular>
                        tCO2e/{sitesEmissions?.intensityRevenueUnit}
                      </Label12Regular>
                    </span>
                  </Box>
                ) : (
                  <Label12Regular
                    style={{ color: "#C1C5CE", fontStyle: "italic" }}
                  >
                    No data provided
                  </Label12Regular>
                )}
              </Box>
              <Box sx={{ p: "16px", paddingTop: 0 }}>
                <Label16SemiBold>Scope 1 + Scope 2 (Market)</Label16SemiBold>
                {sitesEmissions?.totalMarketIntensityRevenueCo2Emission !==
                  null ? (
                  <Box sx={{ display: "flex", mt: "7px" }}>
                    <Label16Medium>
                      {sitesEmissions?.totalMarketIntensityRevenueCo2Emission}
                    </Label16Medium>
                    <span style={{ marginTop: "2px", marginLeft: "7px" }}>
                      <Label12Regular>
                        tCO2e/{sitesEmissions?.intensityRevenueUnit}
                      </Label12Regular>
                    </span>
                  </Box>
                ) : (
                  <Label12Regular
                    style={{ color: "#C1C5CE", fontStyle: "italic" }}
                  >
                    No data provided
                  </Label12Regular>
                )}
              </Box>
            </Box>
          </Box>
          <Box sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            borderRadius: "8px",
            border: "1px solid rgba(5, 65, 84, 0.5)",
          }}>
            <Label18SemiBold
              sx={{ fontSize: 18, fontWeight: "700", color: "#054254", py: "8px", pl: "16px" }}
            >
              Area
            </Label18SemiBold>
            <Box sx={{
              borderTop: "1px solid rgba(5, 65, 84, 0.5)",
              display: "flex",
              flexDirection: "column",
            }}>
              <Box sx={{ p: "16px" }}>
                <Label16SemiBold>
                  Scope 1 + Scope 2 (Location)
                </Label16SemiBold>
                {sitesEmissions?.totalIntensityAreaCo2Emission !== null ? (
                  <Box sx={{ display: "flex", mt: "7px" }}>
                    <Label16Medium>
                      {sitesEmissions?.totalIntensityAreaCo2Emission}
                    </Label16Medium>
                    <span style={{ marginTop: "2px", marginLeft: "7px" }}>
                      <Label12Regular>
                        tCO2e/{sitesEmissions?.intensityAreaUnit}
                      </Label12Regular>
                    </span>
                  </Box>
                ) : (
                  <Label12Regular
                    style={{ color: "#C1C5CE", fontStyle: "italic" }}
                  >
                    No data provided
                  </Label12Regular>
                )}
              </Box>
              <Box sx={{ p: "16px", paddingTop: 0 }}>
                <Label16SemiBold>Scope 1 + Scope 2 (Market)</Label16SemiBold>
                {sitesEmissions?.totalMarketIntensityAreaCo2Emission !==
                  null ? (
                  <Box sx={{ display: "flex", mt: "7px" }}>
                    <Label16Medium>
                      {sitesEmissions?.totalMarketIntensityAreaCo2Emission}
                    </Label16Medium>
                    <span style={{ marginTop: "2px", marginLeft: "7px" }}>
                      <Label12Regular>
                        tCO2e/{sitesEmissions?.intensityAreaUnit}
                      </Label12Regular>
                    </span>
                  </Box>
                ) : (
                  <Label12Regular
                    style={{ color: "#C1C5CE", fontStyle: "italic" }}
                  >
                    No data provided
                  </Label12Regular>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
);
